import {useEffect, useState} from "react";
import * as React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useBoundStore} from "../../../Store/store";
import {BaseCard} from "../../../Components/Cards/BaseCard";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import {PrimaryButton} from "../../../Components/Buttons/PrimaryButton";
import {useTranslation} from "react-i18next";
import {DangerButton} from "../../../Components/Buttons/DangerButton";
import {TextInput} from "../../../Components/Form/TextInput";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ContractsTable} from "../../../Components/Tables/TableCards/ContractsTable";
import {InvoicesTable} from "../../../Components/Tables/TableCards/InvoicesTable";
import {PerformanceReportsTable} from "../../../Components/Tables/TableCards/PerformanceReportsTable";
import {ShiftsTable} from "../../../Components/Tables/TableCards/ShiftsTable";
import {DeleteConfirmDialog} from "../../../Components/Dialogs/Confirmation/DeleteConfirmDialog";
import toast from "react-hot-toast";
import {ControlledSwitch} from "../../../Components/Form/ControlledSwitch";
import {BranchesTable} from "../../../Components/Tables/TableCards/BranchesTable";
import {MailHistoryList} from "../../../Components/MailHistory/MailHistoryList";

export const AdminCustomerDetails = () => {
    const {
        customerId,
    } = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const getCustomerDetail = useBoundStore((state) => state.getCustomerDetail);
    const customerDetail = useBoundStore((state) => state.customerDetail);
    const deleteCustomer = useBoundStore((state) => state.deleteCustomer);
    const updateCustomer = useBoundStore((state) => state.updateCustomer);
    const markInvoiceAsPaid = useBoundStore((state) => state.markInvoiceAsPaid);

    const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
    const [forceRenewData, setForceRenewData] = useState(false);
    const [customerLoading, setCustomerLoading] = useState(true);

    const handleDelete = () => {
        deleteCustomer(customerId).then(() => {
            setOpenDeleteConfirm(false);
            toast.success(t('customers.toast_deleted'));
            navigate('/admin/customers');
        })
    }

    const handleMarkAsPaid = async (invoiceId) => {
        const success = await markInvoiceAsPaid(invoiceId);
        if (success) {
            setForceRenewData(!forceRenewData);
        }
        return success;
    };

    useEffect(() => {
        const fetchCustomerDetails = async () => {
            setCustomerLoading(true);
            await getCustomerDetail(customerId);
            setCustomerLoading(false);
        };

        fetchCustomerDetails();
    }, [getCustomerDetail, customerId]);

    useEffect(() => {
        if(customerDetail){
            reset({
                firstname: customerDetail?.firstname ?? null,
                lastname: customerDetail?.lastname ?? null,
                email: customerDetail?.email ?? null,
                is_internal_client: customerDetail?.is_internal_client === 0 ? false : true ?? false,
            })
        }
    }, [customerDetail?.id]);

    const schema = yup.object({
        firstname: yup.string().required(),
        lastname: yup.string().required(),
        email: yup.string().email().required(),
        is_internal_client: yup.boolean().nullable(),
    }).required()

    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        setValue,
        control,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            firstname: customerDetail?.firstname ?? null,
            lastname: customerDetail?.lastname ?? null,
            email: customerDetail?.email ?? null,
            is_internal_client: customerDetail?.is_internal_client === 0 ? false : true ?? false,
        }
    });


    const onSubmit = (data) => {
        const transformedData = {
            ...data,
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
            customer_id: customerId,
            is_published: data.published === "" ? false : data.published,
            contract_type: data.contractType,
            health_institution: data.company_name,
            is_internal_client: data.is_internal_client ? 1 : 0,
        }

        updateCustomer(customerId, transformedData).then(() => {
            toast.success(t('customers.toast_updated'));
        });
    }

    if (customerLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between mb-4 items-center'}>
                    <div>
                        <h1 className={'text-2xl font-semibold'}>{t('general.labels.customer_details')}</h1>
                        <div className={"flex flex-row group items-center"} onClick={() => {
                            navigate('/admin/customers');
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft}
                                                  className={'text-sm transition-all group-hover:mr-2 duration-500 cursor-pointer'}/>

                            <div className={'text-sm text-gray-500 my-auto cursor-pointer ml-2'}>{t('general.actions.back_to_overview')}</div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-4">
                        <DangerButton onClick={() => setOpenDeleteConfirm(true)}>{t('general.actions.remove_customer')}</DangerButton>
                    </div>
                </div>
            </div>
            <div className={'grid grid-cols-1 md:grid-cols-3 md:space-x-4 space-y-4 md:space-y-0'}>
                <div className={'md:col-span-3 space-y-4'}>
                    <BaseCard border={false}>
                        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                            <div className={'flex flex-col md:flex-row justify-between'}>
                                <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('general.labels.global_information')}</h2>

                                <div className="mb-2 mt-2">
                                    <ControlledSwitch
                                        control={control}
                                        name={'is_internal_client'}
                                        getValues={getValues}
                                        errors={errors}
                                        setValue={setValue}
                                        translationIndex={'general.fields.is_internal_client'}
                                    />
                                </div>

                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'firstname'}
                                    register={register}
                                    translationIndex={'general.fields.firstname'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'lastname'}
                                    register={register}
                                    translationIndex={'general.fields.lastname'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <TextInput
                                    name={'email'}
                                    register={register}
                                    translationIndex={'general.fields.email'}
                                    errors={errors}
                                />
                            </div>

                            <div className={'mt-4'}>
                                <PrimaryButton onClick={handleSubmit(onSubmit)}>
                                    {t('branches.save_customer')}
                                </PrimaryButton>
                            </div>
                        </form>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('branches.title')}</h2>
                        <BranchesTable
                            userId={customerId}
                            forceNewData={forceRenewData}
                            shouldUpdate={() => setForceRenewData(!forceRenewData)}
                        />
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('shifts.title')}</h2>
                        <ShiftsTable userId={customerId} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('contracts.title')}</h2>
                        <ContractsTable userId={customerId} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                    <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('invoices.title')}</h2>
                        <InvoicesTable userId={customerId} showActions={true} onMarkAsPaid={handleMarkAsPaid} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('performance_reports.title')}</h2>
                        <PerformanceReportsTable userId={customerId} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <MailHistoryList filters={{customerId: customerId}} />
                    </BaseCard>
                </div>
            </div>

            <DeleteConfirmDialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)} onConfirm={handleDelete}/>
        </div>
    )
}

